import { generatePdfUint8Array } from '../pdfUtilities';
import { toPdfStr } from '../../utils';
import { TEstimateBaseItem } from '../../types/estimate/estimate';
import { roundDown } from '../../utils/estimate';
import { ESTIMATE_ITEM_MAX_PER_PDF_PAGE } from '../../const/Const';
import { Template } from '@pdfme/common';
import { PDFDocument } from 'pdf-lib';
import { calcAllSellingPrice } from '../../utils/calcRules';

export const mergeEstimateDetails = async (
  data: any,
  font: any,
  mergedPdf: PDFDocument
) => {
  const filteredEstimates = data.estimates.filter(
    (estimate: any) => estimate.code !== '見積書なし'
  );
  // estimatesの各要素に対してinvoiceEstimatePdfを実行
  const estimatePdfPages = [];
  for (let i = 0; i < filteredEstimates.length; i++) {
    const estimatePage = await invoiceEstimatePdf(
      data,
      filteredEstimates[i].items,
      filteredEstimates[i].code,
      filteredEstimates[i].display_item_number_in_pdf,
      font
    );
    estimatePdfPages.push(estimatePage);
  }

  for (const estimatePdfPage of estimatePdfPages) {
    const pdfDoc2 = await PDFDocument.load(estimatePdfPage);
    const copiedPages2 = await mergedPdf.copyPages(
      pdfDoc2,
      pdfDoc2.getPageIndices()
    );
    copiedPages2.forEach(page => mergedPdf.addPage(page));
  }
};

const invoiceEstimatePdf = async (
  data: any,
  estimateData: any,
  code: any,
  displayItemNumber: boolean,
  font: any
) => {
  // 初回読み込みの容量を減らすため動的インポート
  const basePdf2 = await import('../../assets/estimates_base_pdf2');

  // 階層別に振り分け
  const mainItems = estimateData.filter((item: TEstimateBaseItem) => {
    return item.layer === 1;
  });
  const allSecondItems = estimateData.filter((item: TEstimateBaseItem) => {
    return item.layer === 2;
  });
  const allThirdItems = estimateData.filter((item: TEstimateBaseItem) => {
    return item.layer === 3;
  });
  const allFourthItems = estimateData.filter((item: TEstimateBaseItem) => {
    return item.layer === 4;
  });
  const allFifthItems = estimateData.filter((item: TEstimateBaseItem) => {
    return item.layer === 5;
  });

  // テンプレート
  const schemas: any = {};
  // 項目書
  const itemInputs: any = [];
  // 明細書
  const secondlItemInputs: any = [];

  schemas['code'] = {
    // NO
    type: 'text',
    position: {
      x: 8.9,
      y: 10.06
    },
    width: 59.87,
    height: 6.2,
    alignment: 'left',
    fontSize: 10,
    characterSpacing: 0,
    lineHeight: 1.5
  };
  schemas['title'] = {
    // 名称
    type: 'text',
    position: {
      x: 114.3,
      y: 14.06
    },
    width: 66.48,
    height: 4.88,
    alignment: 'center',
    fontSize: 10,
    characterSpacing: 0,
    lineHeight: 1.5
  };
  schemas['total_price'] = {
    // 小計
    type: 'text',
    position: {
      x: 181,
      y: 193.94
    },
    width: 40,
    height: 6.2,
    alignment: 'right',
    fontSize: 10,
    characterSpacing: 0,
    lineHeight: 1
  };
  schemas[`breadcrumbs`] = {
    type: 'text',
    position: {
      x: 13.2,
      y: 20.64
    },
    width: 240,
    height: 4.5,
    alignment: 'left',
    fontSize: 9,
    characterSpacing: 0,
    lineHeight: 1.9
  };
  schemas['page_no'] = {
    type: 'text',
    position: {
      x: 192,
      y: 203
    },
    width: 100,
    height: 6.2,
    alignment: 'right',
    fontSize: 10,
    characterSpacing: 0,
    lineHeight: 1
  };
  [...Array(24)].forEach((_: any, i: number) => {
    schemas[`item_no${i}`] = {
      type: 'text',
      position: {
        x: 10.2,
        y: 36 + 6.55 * i
      },
      width: 14,
      height: 4.5,
      alignment: 'center',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_name${i}`] = {
      type: 'text',
      position: {
        x: 23.1,
        y: 36.5 + 6.55 * i
      },
      width: 66,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.9
    };
    // Schema when the number of characters is 30 or more
    // first line
    schemas[`item_spec${i}_top`] = {
      type: 'text',
      position: {
        x: 86.5,
        y: 35.5 + 6.55 * i
      },
      width: 66,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.5
    };
    // 2nd line
    schemas[`item_spec${i}_seconds`] = {
      type: 'text',
      position: {
        x: 86.5,
        y: 38.15 + 6.55 * i
      },
      width: 66,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.5
    };
    schemas[`item_spec${i}`] = {
      type: 'text',
      position: {
        x: 86.5,
        y: 36.5 + 6.55 * i
      },
      width: 66,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.9
    };
    schemas[`item_quantity${i}`] = {
      type: 'text',
      position: {
        x: 152,
        y: 36 + 6.55 * i
      },
      width: 12.5,
      height: 6.4,
      alignment: 'center',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_unit${i}`] = {
      type: 'text',
      position: {
        x: 165,
        y: 36 + 6.55 * i
      },
      width: 14,
      height: 6.4,
      alignment: 'center',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_unit_price${i}`] = {
      type: 'text',
      position: {
        x: 173,
        y: 36 + 6.55 * i
      },
      width: 26,
      height: 6.4,
      alignment: 'right',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_all_selling_price${i}`] = {
      type: 'text',
      position: {
        x: 195,
        y: 36 + 6.55 * i
      },
      width: 26,
      height: 6.4,
      alignment: 'right',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_remarks${i}_top`] = {
      type: 'text',
      position: {
        x: 223,
        y: 35.5 + 6.55 * i
      },
      width: 86,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.5
    };
    schemas[`item_remarks${i}_seconds`] = {
      type: 'text',
      position: {
        x: 223,
        y: 38.15 + 6.55 * i
      },
      width: 86,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.5
    };
    schemas[`item_remarks${i}`] = {
      type: 'text',
      position: {
        x: 223,
        y: 36.5 + 6.55 * i
      },
      width: 86,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.9
    };
  });

  // 第一階層の表示に必要なページ数
  const pageNum = Math.ceil(mainItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE);

  // ページ番号
  let pageNo = 0;

  // タイトル、見積書番号、ページ番号、「次ページに続く」を第一階層のページに追加
  for (let i = 0; i < pageNum; i++) {
    pageNo += 1;
    itemInputs.push({
      title: '＜ご　請　求　明　細＞',
      code: `詳細No. ${code}`,
      page_no: String(pageNo),
      total_price: '次ページに続く'
    });
  }

  // 第一階層の小計
  let items_total_price = 0;
  mainItems.forEach((item: TEstimateBaseItem, i: number) => {
    // 1ページのオブジェクト
    const firstObj = itemInputs[Math.floor(i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE)];

    // 1ページ内でのインデックス
    const indexInSheet = i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

    // 第二階層の小計
    let second_items_total_price = 0;
    
    firstObj[`item_no${indexInSheet}`] = displayItemNumber ? String(i + 1) : "";
    firstObj[`item_name${indexInSheet}`] = toPdfStr(item.name);
    // 0と空欄の判別
    const Quantity = item.quantity
      ? Number(item.quantity).toLocaleString('ja-JP')
      : item.quantity === 0
      ? ''
      : '0';
    firstObj[`item_quantity${indexInSheet}`] = Quantity;
    firstObj[`item_unit${indexInSheet}`] = item.unit_name;
    const itemSellingUnitPrice =
      item.selling_unit_price != 0
        ? Number(item.selling_unit_price).toLocaleString('ja-JP')
        : '';
    firstObj[`item_unit_price${indexInSheet}`] = itemSellingUnitPrice;
    // 小数点以下切り捨て
    const itemAllSellingPrice: number = calcAllSellingPrice(
      Number(item.selling_unit_price),
      Number(item.quantity)
    );
    if (itemAllSellingPrice != 0) {
      firstObj[`item_all_selling_price${indexInSheet}`] =
        itemAllSellingPrice.toLocaleString('ja-JP');
    } else {
      firstObj[`item_all_selling_price${indexInSheet}`] = '';
    }
    // spec format
    const itemSpec: string = toPdfStr(item.spec);
    const specIndex = itemSpec.search(/\r\n|\n|\r/);
    if (specIndex !== -1) {
      // 改行が含まれる場合
      firstObj[`item_spec${indexInSheet}_top`] = itemSpec.substring(
        0,
        specIndex
      );
      firstObj[`item_spec${indexInSheet}_seconds`] = itemSpec.substring(
        specIndex + 1
      );
    } else if (itemSpec.length > 20) {
      // Processing when the number of characters is 20 or more
      firstObj[`item_spec${indexInSheet}_top`] = itemSpec.substr(0, 20);
      firstObj[`item_spec${indexInSheet}_seconds`] = itemSpec.substr(20, 20);
    } else {
      firstObj[`item_spec${indexInSheet}`] = itemSpec;
    }
    //remarks format
    const itemRemarks: string = toPdfStr(item.remarks);
    const remarksIndex = itemRemarks.search(/\r\n|\n|\r/);
    if (remarksIndex !== -1) {
      // 改行が含まれる場合
      firstObj[`item_remarks${indexInSheet}_top`] = itemRemarks.substring(
        0,
        remarksIndex
      );
      firstObj[`item_remarks${indexInSheet}_seconds`] = itemRemarks.substring(
        remarksIndex + 1
      );
    } else if (itemRemarks.length > 20) {
      // Processing when the number of characters is 15 or more
      firstObj[`item_remarks${indexInSheet}_top`] = itemRemarks.substr(0, 20);
      firstObj[`item_remarks${indexInSheet}_seconds`] = itemRemarks.substr(
        20,
        20
      );
    } else {
      firstObj[`item_remarks${indexInSheet}`] = itemRemarks;
    }

    // 明細書 - 第二階層
    if (item.has_under_layer_flg) {
      //１階層が明細ありの場合、idが位置する２階層を取得
      const secondItems = allSecondItems.filter(
        (allSecondItem: TEstimateBaseItem) => {
          return allSecondItem.parent_item_id === item.id;
        }
      );
      if (secondItems.length > 0) {
        // 第二階層を表示するのに必要な枚数
        const secondPageNum = Math.ceil(
          secondItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
        );

        // 第二階層を表示するのに必要な枚数のシートを用意する。
        const secondSheets: any = [];
        for (let k = 0; k < secondPageNum; k++) {
          pageNo += 1;
          secondSheets.push({
            title: '＜ご　請　求　明　細＞',
            code: `詳細No. ${code}`,
            // パンくず
            breadcrumbs: `${String(i + 1)}. ${item.name}`,
            page_no: String(pageNo),
            total_price: '次ページに続く'
          });
        }

        // 三階層格納用
        const thirdItemInputs: any = [];

        secondItems.forEach((secondItem: any, second_i: number) => {
          // 1ページのオブジェクト
          const secondObj =
            secondSheets[Math.floor(second_i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE)];

          // 1ページ内でのインデックス
          const secondIndexInSheet = second_i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

          secondObj[`item_no${secondIndexInSheet}`] = displayItemNumber ? String(second_i + 1) : "";
          secondObj[`item_name${secondIndexInSheet}`] = toPdfStr(
            secondItem.name
          );
          const secondSpec: string = toPdfStr(secondItem.spec);
          // spec format
          const secondSpecIndex = secondSpec.search(/\r\n|\n|\r/);
          if (secondSpecIndex !== -1) {
            // 改行が含まれる場合
            secondObj[`item_spec${secondIndexInSheet}_top`] =
              secondSpec.substring(0, secondSpecIndex);
            secondObj[`item_spec${secondIndexInSheet}_seconds`] =
              secondSpec.substring(secondSpecIndex + 1);
          } else if (secondSpec.length > 20) {
            // Processing when the number of characters is 20 or more
            secondObj[`item_spec${secondIndexInSheet}_top`] = secondSpec.substr(
              0,
              20
            );
            secondObj[`item_spec${secondIndexInSheet}_seconds`] =
              secondSpec.substr(20, 20);
          } else {
            secondObj[`item_spec${secondIndexInSheet}`] = secondSpec;
          }
          // 0と空欄の判別
          const secondQuantity = secondItem.quantity
            ? Number(secondItem.quantity).toLocaleString('ja-JP')
            : secondItem.quantity === 0
            ? ''
            : '0';
          secondObj[`item_quantity${secondIndexInSheet}`] = secondQuantity;
          secondObj[`item_unit${secondIndexInSheet}`] = secondItem.unit_name;
          const secondItemSellingUnitPrice =
            secondItem.selling_unit_price != 0
              ? Number(secondItem.selling_unit_price).toLocaleString('ja-JP')
              : '';
          secondObj[`item_unit_price${secondIndexInSheet}`] =
            secondItemSellingUnitPrice;
          // 小数点以下切り捨て
          const secondItemAllSellingPrice: number = calcAllSellingPrice(
            Number(secondItem.selling_unit_price),
            Number(secondItem.quantity)
          );
          if (secondItemAllSellingPrice != 0) {
            secondObj[`item_all_selling_price${secondIndexInSheet}`] =
              secondItemAllSellingPrice.toLocaleString('ja-JP');
          } else {
            secondObj[`item_all_selling_price${secondIndexInSheet}`] = '';
          }
          const secondRemarks: string = toPdfStr(secondItem.remarks);
          // remarks format
          const secondRemarksIndex = secondRemarks.search(/\r\n|\n|\r/);
          if (secondRemarksIndex !== -1) {
            // 改行が含まれる場合
            secondObj[`item_remarks${secondIndexInSheet}_top`] =
              secondRemarks.substring(0, secondRemarksIndex);
            secondObj[`item_remarks${secondIndexInSheet}_seconds`] =
              secondRemarks.substring(secondRemarksIndex + 1);
          } else if (secondRemarks.length > 20) {
            // Processing when the number of characters is 15 or more
            secondObj[`item_remarks${secondIndexInSheet}_top`] =
              secondRemarks.substr(0, 20);
            secondObj[`item_remarks${secondIndexInSheet}_seconds`] =
              secondRemarks.substr(20, 20);
          } else {
            secondObj[`item_remarks${secondIndexInSheet}`] = secondItem.remarks;
          }

          // 明細書 - 第三階層
          if (secondItem.has_under_layer_flg) {
            // ２階層が明細ありの場合、idが位置する３階層を取得
            const thirdItems = allThirdItems.filter(
              (allThirdItem: TEstimateBaseItem) => {
                return allThirdItem.parent_item_id === secondItem.id;
              }
            );
            if (thirdItems.length > 0) {
              // 第三階層を表示するのに必要な枚数
              const thirdPageNum = Math.ceil(
                thirdItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
              );

              // 第三階層を表示するのに必要な枚数のシートを用意する。
              const thirdSheets: any = [];
              for (let k = 0; k < thirdPageNum; k++) {
                pageNo += 1;
                thirdSheets.push({
                  title: '＜ご　請　求　明　細＞',
                  code: `詳細No. ${code}`,
                  // パンくず
                  breadcrumbs: `${String(i + 1)}. ${item.name}　>　${String(
                    second_i + 1
                  )}. ${secondItem.name}`,
                  page_no: String(pageNo),
                  total_price: '次ページに続く'
                });
              }
              // 四階層格納用
              const fourthItemInputs: any = [];
              // 第三階層の小計
              let third_items_total_price = 0;

              thirdItems.forEach((thirdItem: any, third_i: number) => {
                // 1ページのオブジェクト
                const thirdObj =
                  thirdSheets[
                    Math.floor(third_i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE)
                  ];

                // 1ページ内でのインデックス
                const thirdIndexInSheet =
                  third_i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

                thirdObj[`item_no${thirdIndexInSheet}`] = displayItemNumber ? String(third_i + 1) : "";
                thirdObj[`item_name${thirdIndexInSheet}`] = toPdfStr(
                  thirdItem.name
                );
                const thirdSpec: string = toPdfStr(thirdItem.spec);
                // spec format
                const thirdSpecIndex = thirdSpec.search(/\r\n|\n|\r/);
                if (thirdSpecIndex !== -1) {
                  // 改行が含まれる場合
                  thirdObj[`item_spec${thirdIndexInSheet}_top`] =
                    thirdSpec.substring(0, thirdSpecIndex);
                  thirdObj[`item_spec${thirdIndexInSheet}_seconds`] =
                    thirdSpec.substring(thirdSpecIndex + 1);
                } else if (thirdSpec.length > 20) {
                  // Processing when the number of characters is 20 or more
                  thirdObj[`item_spec${thirdIndexInSheet}_top`] =
                    thirdSpec.substr(0, 20);
                  thirdObj[`item_spec${thirdIndexInSheet}_seconds`] =
                    thirdSpec.substr(20, 20);
                } else {
                  thirdObj[`item_spec${thirdIndexInSheet}`] = thirdSpec;
                }
                // 0と空欄の判別
                const thirdQuantity = thirdItem.quantity
                  ? Number(thirdItem.quantity).toLocaleString('ja-JP')
                  : thirdItem.quantity === 0
                  ? ''
                  : '0';
                thirdObj[`item_quantity${thirdIndexInSheet}`] = thirdQuantity;
                thirdObj[`item_unit${thirdIndexInSheet}`] = thirdItem.unit_name;
                const thirdItemSellingUnitPrice =
                  thirdItem.selling_unit_price != 0
                    ? Number(thirdItem.selling_unit_price).toLocaleString(
                        'ja-JP'
                      )
                    : '';
                thirdObj[`item_unit_price${thirdIndexInSheet}`] =
                  thirdItemSellingUnitPrice;
                // 小数点以下切り捨て
                const thirdItemAllSellingPrice: number = calcAllSellingPrice(
                  Number(thirdItem.selling_unit_price),
                  Number(thirdItem.quantity)
                );
                if (thirdItemAllSellingPrice != 0) {
                  thirdObj[`item_all_selling_price${thirdIndexInSheet}`] =
                    thirdItemAllSellingPrice.toLocaleString('ja-JP');
                } else {
                  thirdObj[`item_all_selling_price${thirdIndexInSheet}`] = '';
                }
                const thirdRemarks: string = toPdfStr(thirdItem.remarks);
                // remarks format
                const thirdRemarksIndex = thirdRemarks.search(/\r\n|\n|\r/);
                if (thirdRemarksIndex !== -1) {
                  // 改行が含まれる場合
                  thirdObj[`item_remarks${thirdIndexInSheet}_top`] =
                    thirdRemarks.substring(0, thirdRemarksIndex);
                  thirdObj[`item_remarks${thirdIndexInSheet}_seconds`] =
                    thirdRemarks.substring(thirdRemarksIndex + 1);
                } else if (thirdRemarks.length > 20) {
                  // Processing when the number of characters is 15 or more
                  thirdObj[`item_remarks${thirdIndexInSheet}_top`] =
                    thirdRemarks.substr(0, 20);
                  thirdObj[`item_remarks${thirdIndexInSheet}_seconds`] =
                    thirdRemarks.substr(20, 20);
                } else {
                  thirdObj[`item_remarks${thirdIndexInSheet}`] = thirdRemarks;
                }

                // 明細書 - 第四階層
                if (thirdItem.has_under_layer_flg) {
                  // ３階層が明細ありの場合、idが位置する４階層を取得
                  const fourthItems = allFourthItems.filter(
                    (allFourthItem: TEstimateBaseItem) => {
                      return allFourthItem.parent_item_id === thirdItem.id;
                    }
                  );
                  if (fourthItems.length > 0) {
                    // 第四階層を表示するのに必要な枚数
                    const fourthPageNum = Math.ceil(
                      fourthItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
                    );

                    // 第四階層を表示するのに必要な枚数のシートを用意する。
                    const fourthSheets: any = [];
                    for (let k = 0; k < fourthPageNum; k++) {
                      pageNo += 1;
                      fourthSheets.push({
                        title: '＜ご　請　求　明　細＞',
                        code: `詳細No. ${code}`,
                        // パンくず
                        breadcrumbs: `${String(i + 1)}. ${
                          item.name
                        }　>　${String(second_i + 1)}. ${
                          secondItem.name
                        }　>　${String(third_i + 1)}. ${thirdItem.name}`,
                        page_no: String(pageNo),
                        total_price: '次ページに続く'
                      });
                    }
                    // 五階層格納用
                    const fifthItemInputs: any = [];
                    // 第四階層の小計
                    let fourth_items_total_price = 0;

                    fourthItems.forEach((fourthItem: any, fourth_i: number) => {
                      // 1ページのオブジェクト
                      const fourthObj =
                        fourthSheets[
                          Math.floor(fourth_i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE)
                        ];

                      // 1ページ内でのインデックス
                      const fourthIndexInSheet =
                        fourth_i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

                      fourthObj[`item_no${fourthIndexInSheet}`] = displayItemNumber ? String(fourth_i + 1) : "";
                      fourthObj[`item_name${fourthIndexInSheet}`] = toPdfStr(
                        fourthItem.name
                      );
                      const fourthSpec: string = toPdfStr(fourthItem.spec);
                      // spec format
                      const fourthSpecIndex = fourthSpec.search(/\r\n|\n|\r/);
                      if (fourthSpecIndex !== -1) {
                        // 改行が含まれる場合
                        fourthObj[`item_spec${fourthIndexInSheet}_top`] =
                          fourthSpec.substring(0, fourthSpecIndex);
                        fourthObj[`item_spec${fourthIndexInSheet}_seconds`] =
                          fourthSpec.substring(fourthSpecIndex + 1);
                      } else if (fourthSpec.length > 20) {
                        // Processing when the number of characters is 20 or more
                        fourthObj[`item_spec${fourthIndexInSheet}_top`] =
                          fourthSpec.substr(0, 20);
                        fourthObj[`item_spec${fourthIndexInSheet}_seconds`] =
                          fourthSpec.substr(20, 20);
                      } else {
                        fourthObj[`item_spec${fourthIndexInSheet}`] =
                          fourthSpec;
                      }
                      // 0と空欄の判別
                      const fourthQuantity = fourthItem.quantity
                        ? Number(fourthItem.quantity).toLocaleString('ja-JP')
                        : fourthItem.quantity === 0
                        ? ''
                        : '0';
                      fourthObj[`item_quantity${fourthIndexInSheet}`] =
                        fourthQuantity;
                      fourthObj[`item_unit${fourthIndexInSheet}`] =
                        fourthItem.unit_name;
                      const fourthItemSellingUnitPrice =
                        fourthItem.selling_unit_price != 0
                          ? Number(
                              fourthItem.selling_unit_price
                            ).toLocaleString('ja-JP')
                          : '';
                      fourthObj[`item_unit_price${fourthIndexInSheet}`] =
                        fourthItemSellingUnitPrice;
                      // 小数点以下切り捨て
                      const fourthItemAllSellingPrice: number =
                        calcAllSellingPrice(
                          Number(fourthItem.selling_unit_price),
                          Number(fourthItem.quantity)
                        );
                      if (fourthItemAllSellingPrice != 0) {
                        fourthObj[
                          `item_all_selling_price${fourthIndexInSheet}`
                        ] = fourthItemAllSellingPrice.toLocaleString('ja-JP');
                      } else {
                        fourthObj[
                          `item_all_selling_price${fourthIndexInSheet}`
                        ] = '';
                      }
                      const fourthRemarks: string = toPdfStr(
                        fourthItem.remarks
                      );
                      // remarks format
                      const fourthRemarksIndex =
                        fourthRemarks.search(/\r\n|\n|\r/);
                      if (fourthRemarksIndex !== -1) {
                        // 改行が含まれる場合
                        fourthObj[`item_remarks${fourthIndexInSheet}_top`] =
                          fourthRemarks.substring(0, fourthRemarksIndex);
                        fourthObj[`item_remarks${fourthIndexInSheet}_seconds`] =
                          fourthRemarks.substring(fourthRemarksIndex + 1);
                      } else if (fourthItem.remarks.length > 20) {
                        // Processing when the number of characters is 15 or more
                        fourthObj[`item_remarks${fourthIndexInSheet}_top`] =
                          fourthRemarks.substr(0, 20);
                        fourthObj[`item_remarks${fourthIndexInSheet}_seconds`] =
                          fourthRemarks.substr(20, 20);
                      } else {
                        fourthObj[`item_remarks${fourthIndexInSheet}`] =
                          fourthRemarks;
                      }

                      // 明細書 - 第五階層
                      if (fourthItem.has_under_layer_flg) {
                        // ４階層が明細ありの場合、idが位置する５階層を取得
                        const fifthItems = allFifthItems.filter(
                          (allFifthItem: TEstimateBaseItem) => {
                            return (
                              allFifthItem.parent_item_id === fourthItem.id
                            );
                          }
                        );
                        if (fifthItems.length > 0) {
                          // 第五階層を表示するのに必要な枚数
                          const fifthPageNum = Math.ceil(
                            fifthItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
                          );

                          // 第五階層を表示するのに必要な枚数のシートを用意する。
                          const fifthSheets: any = [];
                          for (let k = 0; k < fifthPageNum; k++) {
                            pageNo += 1;
                            fifthSheets.push({
                              title: '＜ご　請　求　明　細＞',
                              code: `詳細No. ${code}`,
                              breadcrumbs: `${String(i + 1)}. ${
                                item.name
                              }　>　${String(second_i + 1)}. ${
                                secondItem.name
                              }　>　${String(third_i + 1)}. ${
                                thirdItem.name
                              } 　>　 ${String(fourth_i + 1)}. ${
                                fourthItem.name
                              }`,
                              page_no: String(pageNo),
                              total_price: '次ページに続く'
                            });
                          }
                          // 第五階層の小計
                          let fifth_items_total_price = 0;

                          fifthItems.forEach(
                            (fifthItem: any, fifth_i: number) => {
                              // 1ページのオブジェクト
                              const fifthObj =
                                fifthSheets[
                                  Math.floor(
                                    fifth_i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
                                  )
                                ];

                              // 1ページ内でのインデックス
                              const fifthIndexInSheet =
                                fifth_i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

                              fifthObj[`item_no${fifthIndexInSheet}`] = displayItemNumber ? String(fifth_i + 1) : "";
                              fifthObj[`item_name${fifthIndexInSheet}`] =
                                toPdfStr(fifthItem.name);
                              const fifthSpec: string = toPdfStr(
                                fifthItem.spec
                              );
                              // spec format
                              const fifthSpecIndex =
                                fifthSpec.search(/\r\n|\n|\r/);
                              if (fifthSpecIndex !== -1) {
                                // 改行が含まれる場合
                                fifthObj[`item_spec${fifthIndexInSheet}_top`] =
                                  fifthSpec.substring(0, fifthSpecIndex);
                                fifthObj[
                                  `item_spec${fifthIndexInSheet}_seconds`
                                ] = fifthSpec.substring(fifthSpecIndex + 1);
                              } else if (fifthSpec.length > 20) {
                                // Processing when the number of characters is 20 or more
                                fifthObj[`item_spec${fifthIndexInSheet}_top`] =
                                  fifthSpec.substr(0, 20);
                                fifthObj[
                                  `item_spec${fifthIndexInSheet}_seconds`
                                ] = fifthSpec.substr(20, 20);
                              } else {
                                fifthObj[`item_spec${fifthIndexInSheet}`] =
                                  fifthSpec;
                              }
                              // 0と空欄の判別
                              const fifthQuantity = fifthItem.quantity
                                ? Number(fifthItem.quantity).toLocaleString(
                                    'ja-JP'
                                  )
                                : fifthItem.quantity === 0
                                ? ''
                                : '0';
                              fifthObj[`item_quantity${fifthIndexInSheet}`] =
                                fifthQuantity;
                              fifthObj[`item_unit${fifthIndexInSheet}`] =
                                fifthItem.unit_name;
                              const fifthItemSellingUnitPrice =
                                fifthItem.selling_unit_price != 0
                                  ? Number(
                                      fifthItem.selling_unit_price
                                    ).toLocaleString('ja-JP')
                                  : '';
                              fifthObj[`item_unit_price${fifthIndexInSheet}`] =
                                fifthItemSellingUnitPrice;
                              // 小数点以下切り捨て
                              const fifthItemAllSellingPrice: number =
                                calcAllSellingPrice(
                                  Number(fifthItem.selling_unit_price),
                                  Number(fifthItem.quantity)
                                );
                              if (fifthItemAllSellingPrice != 0) {
                                fifthObj[
                                  `item_all_selling_price${fifthIndexInSheet}`
                                ] =
                                  fifthItemAllSellingPrice.toLocaleString(
                                    'ja-JP'
                                  );
                              } else {
                                fifthObj[
                                  `item_all_selling_price${fifthIndexInSheet}`
                                ] = '';
                              }
                              const fifthRemarks: string = toPdfStr(
                                fifthItem.remarks
                              );
                              // remarks format
                              const fifthRemarksIndex =
                                fifthRemarks.search(/\r\n|\n|\r/);
                              if (fifthRemarksIndex !== -1) {
                                // 改行が含まれる場合
                                fifthObj[
                                  `item_remarks${fifthIndexInSheet}_top`
                                ] = fifthRemarks.substring(
                                  0,
                                  fifthRemarksIndex
                                );
                                fifthObj[
                                  `item_remarks${fifthIndexInSheet}_seconds`
                                ] = fifthRemarks.substring(
                                  fifthRemarksIndex + 1
                                );
                              } else if (fifthRemarks.length > 20) {
                                // Processing when the number of characters is 15 or more
                                fifthObj[
                                  `item_remarks${fifthIndexInSheet}_top`
                                ] = fifthRemarks.substr(0, 20);
                                fifthObj[
                                  `item_remarks${fifthIndexInSheet}_seconds`
                                ] = fifthRemarks.substr(20, 20);
                              } else {
                                fifthObj[`item_remarks${fifthIndexInSheet}`] =
                                  fifthRemarks;
                              }
                              fifth_items_total_price +=
                                fifthItemAllSellingPrice;
                            }
                          );
                          // 第五階層小計
                          fifthSheets[fifthSheets.length - 1]['total_price'] =
                            '小計　　' +
                            Number(fifth_items_total_price).toLocaleString(
                              'ja-JP'
                            );
                          fifthItemInputs.push(...fifthSheets);

                          // 四階層の金額更新
                          const newFourthItemAllSellingPrice =
                            calcAllSellingPrice(
                              fifth_items_total_price,
                              Number(fourthItem.quantity)
                            );
                          fourthObj[
                            `item_all_selling_price${fourthIndexInSheet}`
                          ] =
                            newFourthItemAllSellingPrice !== 0
                              ? newFourthItemAllSellingPrice.toLocaleString(
                                  'ja-JP'
                                )
                              : '';
                          fourth_items_total_price +=
                            newFourthItemAllSellingPrice;
                        }
                      } else {
                        // 五階層を持たない場合は（単価*数量）を小計に加える
                        fourth_items_total_price += fourthItemAllSellingPrice;
                      }
                    });
                    // 第四階層小計
                    fourthSheets[fourthSheets.length - 1]['total_price'] =
                      '小計　　' +
                      Number(fourth_items_total_price).toLocaleString('ja-JP');
                    fourthItemInputs.push(...fourthSheets, ...fifthItemInputs);

                    // 三階層の金額更新
                    const newThirdItemAllSellingPrice = calcAllSellingPrice(
                      fourth_items_total_price,
                      Number(thirdItem.quantity)
                    );
                    thirdObj[`item_all_selling_price${thirdIndexInSheet}`] =
                      newThirdItemAllSellingPrice !== 0
                        ? newThirdItemAllSellingPrice.toLocaleString('ja-JP')
                        : '';
                    third_items_total_price += newThirdItemAllSellingPrice;
                  }
                } else {
                  // 四階層を持たない場合は（単価*数量）を小計に加える
                  third_items_total_price += thirdItemAllSellingPrice;
                }
              });
              // 第三階層小計
              thirdSheets[thirdSheets.length - 1]['total_price'] =
                '小計　　' +
                Number(third_items_total_price).toLocaleString('ja-JP');
              thirdItemInputs.push(...thirdSheets, ...fourthItemInputs);

              // 二階層の金額更新
              const newSecondItemAllSellingPrice = calcAllSellingPrice(
                third_items_total_price,
                Number(secondItem.quantity)
              );
              secondObj[`item_all_selling_price${secondIndexInSheet}`] =
                newSecondItemAllSellingPrice !== 0
                  ? newSecondItemAllSellingPrice.toLocaleString('ja-JP')
                  : '';
              second_items_total_price += newSecondItemAllSellingPrice;
            }
          } else {
            // 三階層を持たない場合は（単価*数量）を小計に加える
            second_items_total_price += secondItemAllSellingPrice;
          }
        });
        // 第二階層小計
        secondSheets[secondSheets.length - 1]['total_price'] =
          '小計　　' + Number(second_items_total_price).toLocaleString('ja-JP');
        secondlItemInputs.push(...secondSheets, ...thirdItemInputs);

        // 親階層の金額を更新し、小計に加える
        const newItemAllSellingPrice = calcAllSellingPrice(
          second_items_total_price,
          Number(item.quantity)
        );
        itemInputs[`item_all_selling_price${indexInSheet}`] =
          newItemAllSellingPrice !== 0
            ? newItemAllSellingPrice.toLocaleString('ja-JP')
            : '';
        items_total_price += newItemAllSellingPrice;
      }
    } else {
      // 二階層を持たない場合は（単価*数量）を小計に加える
      items_total_price += itemAllSellingPrice;
    }
  });

  // 第一階層小計
  itemInputs[itemInputs.length - 1]['total_price'] =
    '小計　　' + Number(items_total_price).toLocaleString('ja-JP');
  const template: Template = {
    // fontName: "mincho",
    basePdf: basePdf2.basePdf,
    schemas: [schemas]
  };
  // const fontData = font;

  const inputs = [...itemInputs, ...secondlItemInputs];

  const pdf = await generatePdfUint8Array(template, inputs, font);

  return pdf;
};
